/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {ServerImage} from "components/ServerImage";
import FeatherIcon from "components/FeatherIcon";
import CompetitionSelectedMark from "pages/Front/Booking/Competitions/components/CompetitionSelectedMark";

interface CompetitionItemCardProps {
    image: string | null;
    title: string;
    categoryType?: string;
    description?: string;
    descriptionHtml?: string;
    price?: React.ReactNode | null;
    infoText?: string;
    isSelected?: boolean;
    action?: React.ReactNode;
    children?: React.ReactNode | React.ReactNode[];
    className?: string;
}

const CompetitionItemCard = (props: CompetitionItemCardProps) => {
    const {t} = useTranslation();
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const hasDetails = Boolean(props.descriptionHtml);

    return (
        <div
            className={clsx(
                "CompetitionItemCard w-full flex flex-col overflow-hidden rounded-xl border-2 transition-colors duration-200",
                props.isSelected ? "border-brand-yellow bg-[#FEFBF2]" : "border-[#E5E5E5] bg-white",
                props.infoText && !props.isSelected ? "border-dashed text-black/60" : "text-black",
                props.className
            )}
        >
            {/* Mobile Layout (up to md) */}
            <div className={"block md:hidden w-full"}>
                <div className={"relative w-full aspect-[3/1] xs:aspect-[4/1] overflow-hidden"}>
                    {props.image && props.image !== "" && (
                        <ServerImage alt={"Competition Image"} className={"w-full h-full object-cover object-center"} src={props.image} />
                    )}
                    <div className={"absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end px-4 py-3"}>
                        <div className={"flex flex-row flex-wrap items-center gap-2"}>
                            {props.isSelected && <CompetitionSelectedMark />}
                            <h3 className={"text-lg font-bold leading-tight text-white"}>{props.title}</h3>
                        </div>
                    </div>
                </div>

                <div className={"flex flex-col gap-y-3 p-4"}>
                    <div className={"flex flex-row flex-wrap items-center gap-2"}>
                        {props.categoryType && (
                            <span className={"rounded bg-[#E5E5E5] px-2 py-1 text-[10px] font-bold whitespace-nowrap"}>{t("Cat.") + props.categoryType}</span>
                        )}
                    </div>

                    {props.description && <p className={"text-xs font-semibold"} dangerouslySetInnerHTML={{__html: props.description}} />}

                    {props.infoText && (
                        <div className={"flex flex-row items-center gap-x-1.5 text-xs font-bold text-black"}>
                            <FeatherIcon name={"alert-circle"} className={"size-5 shrink-0"} />
                            <span>{props.infoText}</span>
                        </div>
                    )}

                    {/* Desktop layout for CompetitionItemCard usually has price, details and action in one row.
                        On mobile, we want price on one side, and (details + action) on the other.
                        If price is missing (like in free competitions), we just have details and action.
                    */}
                    <div className={"flex flex-col gap-3"}>
                        {props?.price && <div className={"w-full flex justify-start"}>{props.price}</div>}
                        <div
                            className={clsx(
                                "flex flex-col xs:flex-row items-center gap-4 w-full pt-4 border-t border-[#D9D9D9]",
                                hasDetails ? "justify-between" : "justify-end"
                            )}
                        >
                            {hasDetails && (
                                <button
                                    className={
                                        "flex flex-row items-center gap-x-1 text-xs font-bold whitespace-nowrap text-black w-full xs:w-auto justify-center xs:justify-start"
                                    }
                                    type={"button"}
                                    onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                                >
                                    <span>{isDetailsOpen ? t("Less") : t("Details")}</span>
                                    <FeatherIcon
                                        className={clsx("size-[22px] transition-transform duration-300", isDetailsOpen && "rotate-180")}
                                        name={"chevron-down"}
                                    />
                                </button>
                            )}
                            {props.action && <div className={"w-full xs:w-auto flex justify-end"}>{props.action}</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Layout (md and up) */}
            <div className={"hidden md:flex flex-row items-center gap-3 md:gap-4 w-full pl-1.5 pr-4 py-1.5"}>
                <div className={"flex flex-row items-start gap-x-4 w-full max-w-[70%]"}>
                    <div className={"size-[90px] shrink-0 overflow-hidden rounded-lg"}>
                        {props.image && props.image !== "" && (
                            <ServerImage alt={"Competition Image"} className={"w-full h-full object-cover object-center flex-none"} src={props.image} />
                        )}
                    </div>

                    <div className={"flex flex-col gap-y-1 grow"}>
                        <div className={"flex flex-row flex-wrap items-center gap-x-3 gap-y-1"}>
                            {props.isSelected && <CompetitionSelectedMark />}
                            <h3 className={"text-base font-bold leading-tight"}>{props.title}</h3>
                            {props.categoryType && (
                                <span className={"rounded bg-[#E5E5E5] px-2 py-1 text-[10px] font-bold whitespace-nowrap"}>
                                    {t("Cat.") + props.categoryType}
                                </span>
                            )}
                        </div>

                        {props.description && (
                            <p className={"CompetitionItemCardDescription text-xs font-semibold"} dangerouslySetInnerHTML={{__html: props.description}} />
                        )}

                        {props.infoText && (
                            <div className={"flex flex-row items-center gap-x-1.5 text-xs font-bold text-black"}>
                                <FeatherIcon name={"alert-circle"} className={"size-5 shrink-0"} />
                                <span>{props.infoText}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={"flex flex-row items-center justify-end w-auto grow gap-x-3 sm:gap-x-5"}>
                    {props.price}

                    {hasDetails && (
                        <button
                            className={"flex flex-row items-center gap-x-1 text-xs font-bold whitespace-nowrap text-black"}
                            type={"button"}
                            onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                        >
                            <span>{isDetailsOpen ? t("Less") : t("Details")}</span>
                            <FeatherIcon
                                className={clsx("size-[22px] transition-transform duration-300", isDetailsOpen && "rotate-180")}
                                name={"chevron-down"}
                            />
                        </button>
                    )}

                    {props.action}
                </div>
            </div>

            {hasDetails && isDetailsOpen && (
                <div className={"CompetitionItemCardDetails flex flex-row border-t border-[#D9D9D9] px-4 py-3 md:pl-1.5 md:pr-4 gap-x-4"}>
                    <div className={"hidden md:block w-[90px] shrink-0"}></div>
                    <div className={"whitespace-pre-line text-xs font-semibold"} dangerouslySetInnerHTML={{__html: props.descriptionHtml as string}} />
                </div>
            )}

            {props.children}
        </div>
    );
};

export default CompetitionItemCard;
